import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Card from "../../atoms/Card";
import { Col, Row } from "react-grid-system";
import TitleSmall from "../../atoms/typography/TitleSmall";
import BodySmall from "../../atoms/typography/BodySmall";
import Body from "../../atoms/typography/Body";
import FilterChip from "../../atoms/inputs/FilterChip";
import ActionButton from "../../atoms/buttons/ActionButton";
import Alert from "../../molecules/Alert";
import { useEffect, useRef, useState } from "react";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import Divider from "../../atoms/Divider";
import InvoiceViewColumn from "../../organisms/validation/InvoiceViewCoulmn";
import AssessableInvoicesList from "../../molecules/assessment/AssessableInvoicesList";
import { useClientAssessmentInvoices } from "../../../graphql/hooks/invoices/useClientAssessmentInvoices";
import { useInvoiceAssessmentDetails } from "../../../graphql/hooks/invoices/useInvoiceAssessmentDetails";
import { useClientStatusCounts } from "../../../graphql/hooks/invoices/useClientStatusCounts";
import { useClient } from "../../../graphql/hooks/useClient";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import IssuerDetailsOverlay from "../../organisms/assessment/IssuerDetailsOverlay";
import RecipientDetailsOverlay from "../../organisms/assessment/RecipientDetailsOverlay";
import RelationshipOverlay from "../../organisms/assessment/RelationshipOverlay";
import Modal from "../../molecules/Modal";
import Subtitle from "../../atoms/typography/Subtitle";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import T800Result from "../../molecules/assessment/T800Result";
import InvoiceDetailsOverview from "../../organisms/assessment/InvoiceDetailsOverview";
import { CheckedStatusCircle } from "../../atoms/StatusCircles";
import {
  InvoiceAssessmentTags,
  type InvoiceTag,
} from "../../../models/InvoiceTags";
import { getInvoiceTagTitleKey } from "../../../utils/invoicesHelpers";
import { formatPrice } from "../../../utils/formatters";
import { useApproveInvoices } from "../../../graphql/hooks/invoices/useApproveInvoices";
import { useForceApproveInvoice } from "../../../graphql/hooks/invoices/useForceApproveInvoice";
import { useReassessInvoices } from "../../../graphql/hooks/invoices/useReassessInvoices";
import type { InvoiceSortBy } from "../../../models/InvoiceSortBy";
import type { SortDirection } from "../../../models/SortDirection";
import SecondaryPageTitleSection from "../../molecules/SecondaryPageTitleSection";
import TextField from "../../atoms/inputs/TextField";
import TextButton from "../../atoms/buttons/TextButton";
import { useClientStatistics } from "../../../graphql/hooks/client/useClientStatistics";
import Small from "../../atoms/typography/Small";
import { OpenLinkIconSmall } from "../../atoms/icons/OpenLinkIcon";
import Subheading from "../../atoms/typography/Subheading";
import RuleOverrideReasonFields from "../../molecules/assessment/RuleOverrideReasonFields";
import OverdueByCurrency from "../../molecules/assessment/OverdueByCurrency";
import type {
  InvoiceAssessmentRuleModel,
  RuleOverrideInput,
} from "../../../models/InvoiceAssessment";

const getFailedRules = (rules?: InvoiceAssessmentRuleModel[]) =>
  rules?.filter((r) => !r.passed && !r.warning) ?? [];

const InvoiceAssessmentDetailedPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { clientIdentifier, invoiceId } = useParams();
  const navigate = useNavigate();
  const displayAssessedInvoice = searchParams.get("displayAssessedInvoice");
  const isDisplayingOldInvoice: boolean = displayAssessedInvoice !== null;
  const from = location.state?.from || "/";

  const detailsRef = useRef<HTMLDivElement>(null);

  const [sortBy, setSortBy] = useState<InvoiceSortBy>("CREATED_AT");
  const [sortDirection, setSortDirection] = useState<SortDirection>("DESC");
  const [searchTerm, setSearchTerm] = useState("");

  const [approvedInvoiceCount, setApprovedInvoiceCount] = useState(0);

  const showApprovedSnackbar = (count: number) => {
    setApprovedInvoiceCount(count);
    setTimeout(() => setApprovedInvoiceCount(0), 5000);
  };

  const onSortChange = (
    newSortBy: InvoiceSortBy,
    newDirection: SortDirection,
  ) => {
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  };

  const {
    invoices,
    loading: invoicesLoading,
    error: invoicesError,
  } = useClientAssessmentInvoices(clientIdentifier ?? "", {
    sortBy,
    sortDirection,
    invoiceStatuses: ["ASSESSMENT"],
    take: undefined,
    searchTerm,
    disabled: isDisplayingOldInvoice,
  });

  const assessableInvoices = invoices?.items ?? [];

  const { client } = useClient(clientIdentifier ?? "");
  const {
    clientStatistics,
    loading: clientStatisticsLoading,
    error: clientStatisticsError,
  } = useClientStatistics(clientIdentifier ?? "SAKNAS");

  const { statusCounts: clientStatusCounts, unhandledDocumentCount } =
    useClientStatusCounts(clientIdentifier);

  const [selectedTag, setSelectedTag] = useState<InvoiceTag | undefined>(
    undefined,
  );
  const toggleTag = (tag: InvoiceTag) =>
    setSelectedTag((prev) => (prev === tag ? undefined : tag));

  const filteredInvoices = selectedTag
    ? assessableInvoices.filter((inv) =>
        inv.tags?.some((t) => t.tag === selectedTag),
      )
    : assessableInvoices;

  const tagCounts = InvoiceAssessmentTags.reduce<Record<string, number>>(
    (acc, tag) => {
      acc[tag] = assessableInvoices.filter((inv) =>
        inv.tags?.some((t) => t.tag === tag),
      ).length;
      return acc;
    },
    {},
  );

  const [selectedInvoice, setSelectedInvoice] = useState<string | undefined>(
    invoiceId,
  );
  const lastSelectedIndexRef = useRef<number>(0);

  useEffect(() => {
    if (isDisplayingOldInvoice || assessableInvoices.length === 0) return;
    const currentIndex = assessableInvoices.findIndex(
      (inv) => inv.id === selectedInvoice,
    );
    if (currentIndex !== -1) {
      lastSelectedIndexRef.current = currentIndex;
    } else {
      const nextIndex = Math.min(
        lastSelectedIndexRef.current,
        assessableInvoices.length - 1,
      );
      setSelectedInvoice(assessableInvoices[nextIndex].id);
    }
  }, [assessableInvoices]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedInvoiceListRow = assessableInvoices.find(
    (i) => i.id === selectedInvoice,
  );
  const { invoice: selectedInvoiceData } = useInvoiceAssessmentDetails(
    clientIdentifier,
    selectedInvoice,
  );
  const selectedInvoiceNumber =
    selectedInvoiceData?.invoiceNumber ?? selectedInvoiceListRow?.invoiceNumber;
  const selectedInvoiceFileId =
    selectedInvoiceData?.invoiceFileId ?? selectedInvoiceListRow?.invoiceFileId;

  const onSelectInvoice = (invoiceId: string) => {
    setSelectedInvoice(invoiceId);
    if (detailsRef.current) {
      const y = detailsRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const onClickPreviousInvoice = () => {
    const currentIndex = assessableInvoices.findIndex(
      (invoice) => invoice.id === selectedInvoice,
    );
    if (currentIndex > 0) {
      setSelectedInvoice(assessableInvoices[currentIndex - 1].id);
    }
  };

  const onClickNextInvoice = () => {
    const currentIndex = assessableInvoices.findIndex(
      (invoice) => invoice.id === selectedInvoice,
    );
    if (currentIndex < assessableInvoices.length - 1) {
      setSelectedInvoice(assessableInvoices[currentIndex + 1].id);
    }
  };

  const [invoiceIdsToApprove, setInvoiceIdsToApprove] = useState<string[]>([]);
  const [ruleReasons, setRuleReasons] = useState<Record<string, string>>({});
  const [approveError, setApproveError] = useState<string[] | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);

  const [newInvoiceAssessmentOpen, setNewInvoiceAssessmentOpen] =
    useState(false);
  const [reassessedInvoiceIds, setReassessedInvoiceIds] = useState<string[]>(
    [],
  );
  const [reassessSource, setReassessSource] = useState<
    "single" | "bulk" | null
  >(null);
  const [currentFailedIndex, setCurrentFailedIndex] = useState(0);
  const wizardStepRef = useRef<{ isLastStep: boolean } | null>(null);
  const [newAssessmentError, setNewAssessmentError] = useState<string[] | null>(
    null,
  );

  const failedReassessedInvoices = assessableInvoices.filter(
    (inv) =>
      reassessedInvoiceIds.includes(inv.id) && !inv.latestAssessment?.passed,
  );
  const newInvoiceAssessmentApproved =
    reassessedInvoiceIds.length > 0 && failedReassessedInvoices.length === 0;

  const { reassessInvoices, loading: reassessLoading } = useReassessInvoices({
    onCompleted: (result) => {
      setReassessSource(null);
      const failures = (result?.invoice?.reassessInvoices ?? []).filter(
        (r) => !r.success,
      );
      if (failures.length > 0) {
        setNewAssessmentError(
          failures.map((f) => f.errorMessage ?? t("errors.somethingWentWrong")),
        );
      } else {
        setNewAssessmentError(null);
        setCurrentFailedIndex(0);
        setNewInvoiceAssessmentOpen(true);
      }
    },
    onError: () => {
      setReassessSource(null);
      setNewAssessmentError([t("errors.somethingWentWrong")]);
    },
  });

  const { approveInvoices, loading: approveInvoicesLoading } =
    useApproveInvoices({
      onCompleted: (result) => {
        const results = result?.invoice?.approveInvoices ?? [];
        const successCount = results.filter((r) => r.success).length;
        if (successCount > 0) {
          showApprovedSnackbar(successCount);
        }
        const failures = results.filter((r) => !r.success);
        if (failures.length > 0) {
          const errorLines = failures.map((f) => {
            const inv = assessableInvoices.find((i) => i.id === f.invoiceId);
            return `${inv?.invoiceNumber ?? f.invoiceId}: ${f.errorMessage}`;
          });
          setApproveError(errorLines);
          setNewAssessmentError(errorLines);
        } else {
          setNewInvoiceAssessmentOpen(false);
          setInvoiceIdsToApprove([]);
          setRuleReasons({});
          setNewAssessmentError(null);
          setApproveError(null);
        }
      },
      onError: () => {
        setNewAssessmentError([t("errors.somethingWentWrong")]);
        setApproveError([t("errors.somethingWentWrong")]);
      },
    });

  const { forceApproveInvoice, loading: forceApproveLoading } =
    useForceApproveInvoice({
      onCompleted: (result) => {
        if (!result?.invoice?.forceApproveInvoice?.success) {
          setApproveError([t("invoiceAssessment.invoiceFailedToApprove")]);
          setNewAssessmentError([
            t("invoiceAssessment.invoiceFailedToApprove"),
          ]);
        } else {
          showApprovedSnackbar(1);
          setInvoiceIdsToApprove([]);
          setRuleReasons({});
          setApproveError(null);
          setNewAssessmentError(null);
          const step = wizardStepRef.current;
          wizardStepRef.current = null;
          if (!step || step.isLastStep) {
            setNewInvoiceAssessmentOpen(false);
            setCurrentFailedIndex(0);
            setReassessedInvoiceIds([]);
          }
        }
      },
      onError: () => {
        setApproveError([t("errors.somethingWentWrong")]);
        setNewAssessmentError([t("errors.somethingWentWrong")]);
      },
    });

  const hasReasonForEveryRule = (rules: InvoiceAssessmentRuleModel[]) =>
    rules.every((r) => !!ruleReasons[r.invoiceAssessmentRuleId]?.trim());

  const toRuleOverrides = (
    rules: InvoiceAssessmentRuleModel[],
  ): RuleOverrideInput[] =>
    rules.map((r) => ({
      invoiceAssessmentRuleId: r.invoiceAssessmentRuleId,
      reason: ruleReasons[r.invoiceAssessmentRuleId].trim(),
    }));

  const onRuleReasonChange = (
    invoiceAssessmentRuleId: string,
    reason: string,
  ) =>
    setRuleReasons((prev) => ({ ...prev, [invoiceAssessmentRuleId]: reason }));

  const handleApprove = (
    t800Approved: boolean,
    invoiceIds: string[],
    rulesToOverride: InvoiceAssessmentRuleModel[],
  ) => {
    if (invoiceIds.length === 0) return;
    setApproveError(null);
    setNewAssessmentError(null);
    if (!t800Approved) {
      if (invoiceIds.length > 1) {
        const message = t(
          "invoiceAssessment.t800MustBeApprovedBeforeApprovingMultipleInvoices",
        );
        setApproveError([message]);
        setNewAssessmentError([message]);
        return;
      }
      if (!hasReasonForEveryRule(rulesToOverride)) {
        setApproveError([t("invoiceAssessment.reasonRequiredForEachRule")]);
        setNewAssessmentError([
          t("invoiceAssessment.reasonRequiredForEachRule"),
        ]);
        return;
      }
      forceApproveInvoice({
        variables: {
          input: {
            invoiceId: invoiceIds[0],
            ruleOverrides: toRuleOverrides(rulesToOverride),
          },
        },
      });
    } else {
      approveInvoices({
        variables: { input: { invoiceIds } },
      });
    }
  };

  const isWizardMode =
    reassessedInvoiceIds.length > 1 && failedReassessedInvoices.length > 0;
  const currentWizardInvoice = isWizardMode
    ? failedReassessedInvoices[currentFailedIndex]
    : undefined;

  const { invoice: assessmentResultInvoice } = useInvoiceAssessmentDetails(
    clientIdentifier,
    isWizardMode ? currentWizardInvoice?.id : failedReassessedInvoices[0]?.id,
  );
  const reassessFailedRules = getFailedRules(
    assessmentResultInvoice?.latestAssessment?.rules,
  );

  const handleWizardApprove = () => {
    if (!currentWizardInvoice) return;
    if (!hasReasonForEveryRule(reassessFailedRules)) {
      setNewAssessmentError([t("invoiceAssessment.reasonRequiredForEachRule")]);
      return;
    }
    setNewAssessmentError(null);
    wizardStepRef.current = {
      isLastStep: currentFailedIndex >= failedReassessedInvoices.length - 1,
    };
    forceApproveInvoice({
      variables: {
        input: {
          invoiceId: currentWizardInvoice.id,
          ruleOverrides: toRuleOverrides(reassessFailedRules),
        },
      },
    });
  };

  const handleWizardSkip = () => {
    setNewAssessmentError(null);
    setRuleReasons({});
    if (currentFailedIndex < failedReassessedInvoices.length - 1) {
      setCurrentFailedIndex((prev) => prev + 1);
    } else {
      setNewInvoiceAssessmentOpen(false);
      setCurrentFailedIndex(0);
      setReassessedInvoiceIds([]);
    }
  };

  const limitDisplay = clientStatistics?.limits?.financialLimit
    ? `${formatPrice(clientStatistics.limits.financialLimit.used, 0)} kr ${t("invoiceAssessment.of").toLowerCase()} ${formatPrice(clientStatistics.limits.financialLimit.limit, 0)} kr`
    : "-";

  const failedRules = getFailedRules(
    selectedInvoiceData?.latestAssessment?.rules,
  );

  const invoiceNumbersToApprove = invoiceIdsToApprove
    .map((id) => assessableInvoices.find((i) => i.id === id)?.invoiceNumber)
    .filter((invoiceNumber): invoiceNumber is string => !!invoiceNumber)
    .join(", ");

  const approveWithoutComment =
    invoiceIdsToApprove.length > 0 &&
    invoiceIdsToApprove.every(
      (id) =>
        assessableInvoices.find((i) => i.id === id)?.latestAssessment?.passed,
    );

  const underValidationCount =
    clientStatusCounts.find((s) => s.status === "VALIDATION")?.count ?? 0;

  return (
    <div style={{ position: "relative", paddingBottom: 64 }}>
      {invoicesError && (
        <Alert
          type="error"
          title={t("errors.couldNotFetchInvoices")}
          message={invoicesError?.message ?? t("errors.somethingWentWrong")}
        />
      )}
      {approvedInvoiceCount > 0 && (
        <Alert
          type="success"
          title={t("invoiceAssessment.invoicesSentForSettlement", {
            count: approvedInvoiceCount,
          })}
          version="snackbar"
        />
      )}
      <SecondaryPageTitleSection
        from={from}
        titleKey={"invoiceAssessment.invoiceAssessment"}
        currentTitleKey="invoices.invoices"
      />

      <HorizontalSpacer spacing={2} />
      {clientStatisticsError && (
        <div>
          <Alert
            type="error"
            title={t("errors.couldNotFetchClientStatistics")}
            message={
              clientStatisticsError?.message ?? t("errors.somethingWentWrong")
            }
            small
          />
          <HorizontalSpacer spacing={0.5} />
        </div>
      )}

      <Card>
        <Row justify="between" align="center" nogutter>
          <div
            style={{
              display: "flex",
              gap: 24,
              padding: 8,
              alignItems: "center",
            }}
          >
            <div
              style={{
                paddingRight: 32,
                borderRight: `1.5px solid ${theme.colors.grey05}`,
              }}
            >
              <TitleSmall color={theme.colors.primary}>
                {client?.name}
              </TitleSmall>
              <TextButton
                onClick={() => navigate(`/client/${clientIdentifier}`)}
                title={clientIdentifier ?? ""}
                icon={<OpenLinkIconSmall />}
                iconPosition="right"
              />
            </div>
            <div
              style={{
                paddingRight: 32,
                borderRight: `1.5px solid ${theme.colors.grey05}`,
                height: "100%",
              }}
            >
              {clientStatisticsLoading ? (
                <>
                  <Small color={theme.colors.textSecondary} fontweight="600">
                    {t("invoiceAssessment.overdue")}
                  </Small>
                  <SmallLoadingSpinner />
                </>
              ) : (
                <OverdueByCurrency
                  overdueByCurrency={
                    clientStatistics?.invoicesStatistics?.maturityStructure
                      ?.overdueByCurrency ?? []
                  }
                />
              )}
            </div>
            <div>
              <Small color={theme.colors.textSecondary} fontweight="600">
                {t("invoiceAssessment.internalFinanceLimit")}
              </Small>
              {clientStatisticsLoading ? (
                <SmallLoadingSpinner />
              ) : (
                <BodySmall
                  color={
                    (clientStatistics?.limits?.financialLimit?.used ?? 0) >
                    (clientStatistics?.limits?.financialLimit?.limit ?? 0)
                      ? theme.colors.warning
                      : theme.colors.text
                  }
                >
                  {limitDisplay}
                </BodySmall>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            {
              <TextButton
                title={t("invoices.unregisteredDocuments", {
                  count: unhandledDocumentCount,
                })}
                onClick={() =>
                  navigate(
                    `/client/${clientIdentifier}/invoicesNew/documents?tab=new`,
                  )
                }
                disabled={unhandledDocumentCount === 0}
              />
            }
            <TextButton
              title={t("invoiceAssessment.numberOfInvoicesUnderValidation", {
                count: underValidationCount,
              })}
              onClick={() =>
                navigate("/invoices/validate", {
                  state: {
                    selectedCompanyId: clientIdentifier,
                    selectedCompanyName: client?.name,
                  },
                })
              }
              disabled={underValidationCount === 0}
            />
          </div>
        </Row>
      </Card>

      <HorizontalSpacer spacing={1.5} />
      <Row align="center" nogutter justify="between">
        {!isDisplayingOldInvoice && (
          <>
            <div style={{ display: "flex", gap: 24 }}>
              {InvoiceAssessmentTags.map((tag) => (
                <FilterChip
                  key={tag}
                  titleKey={`${t(getInvoiceTagTitleKey(tag))} (${tagCounts[tag]})`}
                  selected={selectedTag === tag}
                  onSelect={() => toggleTag(tag)}
                  disabled={tagCounts[tag] === 0}
                />
              ))}
            </div>
            <TextField
              placeholder={t(
                "invoiceAssessment.searchForInvoiceNumberOrRecipient",
              )}
              value={searchTerm}
              onChange={setSearchTerm}
              width={"340px"}
            />
          </>
        )}
      </Row>

      <HorizontalSpacer spacing={1.5} />
      {!isDisplayingOldInvoice && (
        <div>
          {invoicesLoading ? (
            <LoadingSpinner />
          ) : (
            <AssessableInvoicesList
              availableInvoices={filteredInvoices}
              selectedInvoiceId={selectedInvoice}
              selectedInvoiceNumber={selectedInvoiceNumber}
              setSelectedInvoiceId={onSelectInvoice}
              availableClientLimit={
                (clientStatistics?.limits?.financialLimit.used ?? 0) -
                (clientStatistics?.limits?.financialLimit.limit ?? 0)
              }
              clientIdentifier={clientIdentifier ?? ""}
              onApprove={setInvoiceIdsToApprove}
              onReassess={() => {
                setReassessSource("bulk");
                setReassessedInvoiceIds(selectedInvoices);
                reassessInvoices({
                  variables: { input: { invoiceIds: selectedInvoices } },
                });
              }}
              reassessLoading={reassessLoading && reassessSource === "bulk"}
              selectedInvoices={selectedInvoices}
              setSelectedInvoices={setSelectedInvoices}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
            />
          )}
        </div>
      )}

      <div ref={detailsRef}></div>
      <HorizontalSpacer spacing={1} />
      <Row align="center" nogutter justify="between">
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          {failedRules.length > 0 && (
            <Alert
              type="issue"
              title={t("invoiceAssessment.theFollowingErrorsWereIdentified")}
              message={
                <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                  <BodySmall>
                    {failedRules.length} {t("invoiceAssessment.errorsFromT800")}
                  </BodySmall>
                </div>
              }
            />
          )}
        </div>
        <div style={{ display: "flex", gap: 26, alignItems: "center" }}>
          <IssuerDetailsOverlay clientIdentifier={clientIdentifier ?? ""} />
          <RecipientDetailsOverlay
            clientIdentifier={
              selectedInvoiceData?.invoiceRecipient?.client?.id ??
              selectedInvoiceData?.invoiceRecipient?.clientIdentifier ??
              ""
            }
          />
          {clientIdentifier && selectedInvoiceData?.invoiceRecipient && (
            <RelationshipOverlay
              issuerClientIdentifier={clientIdentifier}
              recipientClientIdentifier={
                selectedInvoiceData?.invoiceRecipient?.client?.id ??
                selectedInvoiceData?.invoiceRecipient?.clientIdentifier ??
                ""
              }
              issuerName={client?.name}
              recipientName={selectedInvoiceData?.invoiceRecipient?.name}
            />
          )}
        </div>
      </Row>

      <HorizontalSpacer spacing={1} />
      <Row>
        <Col style={{ minWidth: 0 }}>
          <Card spacing={1.5}>
            <T800Result
              rules={selectedInvoiceData?.latestAssessment?.rules ?? []}
              clientIdentifier={clientIdentifier}
              recipientIdentifier={
                selectedInvoiceData?.invoiceRecipient?.client?.id ??
                selectedInvoiceData?.invoiceRecipient?.clientIdentifier ??
                undefined
              }
              recipientName={selectedInvoiceData?.invoiceRecipient?.name}
              ruleOverrides={
                selectedInvoiceData?.latestAssessment?.ruleOverrides
              }
            />

            <HorizontalSpacer spacing={1} />
            <OutlinedButton
              medium
              title={t("invoiceAssessment.newInvoiceAssessment")}
              icon={<RefreshIcon color={theme.colors.primary} />}
              color={theme.colors.primary}
              loading={reassessLoading && reassessSource === "single"}
              disabled={!selectedInvoice || isDisplayingOldInvoice}
              onClick={() => {
                setReassessSource("single");
                setReassessedInvoiceIds([selectedInvoice!]);
                reassessInvoices({
                  variables: { input: { invoiceIds: [selectedInvoice!] } },
                });
              }}
            />
            <HorizontalSpacer spacing={0.5} />
            <BodySmall color={theme.colors.textSecondary}>
              <i>{t("invoiceAssessment.newInvoiceAssessmentDescription")}</i>
            </BodySmall>
            {newAssessmentError && !newInvoiceAssessmentOpen && (
              <>
                <HorizontalSpacer spacing={0.5} />
                <Alert
                  type="error"
                  title={t("errors.somethingWentWrong")}
                  message={
                    <div>
                      {newAssessmentError.map((msg, i) => (
                        <div key={i}>{msg}</div>
                      ))}
                    </div>
                  }
                />
              </>
            )}
          </Card>

          <HorizontalSpacer spacing={1} />

          <Card spacing={1.5}>
            <InvoiceDetailsOverview
              invoice={selectedInvoiceData}
              clientIdentifier={clientIdentifier}
              isEditable={!isDisplayingOldInvoice}
            />
          </Card>
        </Col>
        <Col>
          <Card>
            <InvoiceViewColumn
              fileId={selectedInvoiceFileId ?? ""}
              noBorder
              invoiceName={selectedInvoiceNumber}
              onClickPreviousInvoice={
                isDisplayingOldInvoice ? undefined : onClickPreviousInvoice
              }
              onClickNextInvoice={
                isDisplayingOldInvoice ? undefined : onClickNextInvoice
              }
              isSelected={
                selectedInvoice
                  ? selectedInvoices.includes(selectedInvoice)
                  : false
              }
              onToggleSelect={
                isDisplayingOldInvoice
                  ? undefined
                  : selectedInvoice
                    ? () =>
                        setSelectedInvoices((prev) =>
                          prev.includes(selectedInvoice)
                            ? prev.filter((id) => id !== selectedInvoice)
                            : [...prev, selectedInvoice],
                        )
                    : undefined
              }
              templateId={selectedInvoiceData?.template?.id}
              clientIdentifier={clientIdentifier}
            />
          </Card>
        </Col>
      </Row>

      {invoiceIdsToApprove.length > 0 && (
        <Modal
          closeIcon
          width={800}
          onClose={() => {
            setInvoiceIdsToApprove([]);
            setRuleReasons({});
            setApproveError(null);
          }}
        >
          <Subtitle color={theme.colors.primary}>
            {t("invoiceAssessment.approveForSettlement")}
          </Subtitle>
          <HorizontalSpacer spacing={1} />
          {invoiceNumbersToApprove && (
            <>
              <Body>
                {t("invoiceAssessment.approveInvoiceNumbers", {
                  count: invoiceIdsToApprove.length,
                  invoiceNumbers: invoiceNumbersToApprove,
                })}
              </Body>
              <HorizontalSpacer spacing={1} />
            </>
          )}
          {!approveWithoutComment && failedRules.length > 0 && (
            <>
              <Subheading color={theme.colors.errorDark} textalign="center">
                {t("invoiceAssessment.theFollowingErrorsWereIdentified")}
              </Subheading>
              <HorizontalSpacer spacing={2} />
              <RuleOverrideReasonFields
                rules={failedRules}
                reasons={ruleReasons}
                onChange={onRuleReasonChange}
              />
            </>
          )}
          {approveError && (
            <Alert
              type="error"
              title={t("errors.somethingWentWrong")}
              message={
                <div>
                  {approveError.map((msg, i) => (
                    <div key={i}>{msg}</div>
                  ))}
                </div>
              }
            />
          )}

          <HorizontalSpacer spacing={1} />
          <Divider />
          <HorizontalSpacer spacing={1} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => {
                setInvoiceIdsToApprove([]);
                setRuleReasons({});
                setApproveError(null);
              }}
            />
            <ActionButton
              title={t("invoiceAssessment.approve")}
              color={theme.colors.primary}
              loading={
                approveWithoutComment
                  ? approveInvoicesLoading
                  : forceApproveLoading
              }
              disabled={
                !approveWithoutComment && !hasReasonForEveryRule(failedRules)
              }
              onClick={() =>
                handleApprove(
                  approveWithoutComment,
                  invoiceIdsToApprove,
                  failedRules,
                )
              }
            />
          </div>
        </Modal>
      )}

      {newInvoiceAssessmentOpen && (
        <Modal
          closeIcon
          width={600}
          onClose={() => {
            setNewInvoiceAssessmentOpen(false);
            setNewAssessmentError(null);
            setRuleReasons({});
            setCurrentFailedIndex(0);
            setReassessedInvoiceIds([]);
          }}
          title={
            isWizardMode
              ? `${t("invoiceAssessment.t800NotApproved")} (${currentFailedIndex + 1}/${failedReassessedInvoices.length})`
              : newInvoiceAssessmentApproved
                ? t("invoiceAssessment.t800Approved")
                : t("invoiceAssessment.t800NotApproved")
          }
          actionButtons={{
            actionButton: {
              buttonTitle: t("invoiceAssessment.approve"),
              onClick: isWizardMode
                ? handleWizardApprove
                : () =>
                    handleApprove(
                      newInvoiceAssessmentApproved,
                      reassessedInvoiceIds,
                      reassessFailedRules,
                    ),
              loading: isWizardMode
                ? forceApproveLoading
                : newInvoiceAssessmentApproved
                  ? approveInvoicesLoading
                  : forceApproveLoading,
              disabled: isWizardMode
                ? !hasReasonForEveryRule(reassessFailedRules)
                : !newInvoiceAssessmentApproved &&
                  !hasReasonForEveryRule(reassessFailedRules),
            },
            outlinedButton: {
              buttonTitle: t("invoiceAssessment.leaveForAssessment"),
              onClick: isWizardMode
                ? handleWizardSkip
                : () => {
                    setNewInvoiceAssessmentOpen(false);
                    setNewAssessmentError(null);
                    setRuleReasons({});
                    setReassessedInvoiceIds([]);
                  },
            },
          }}
        >
          {isWizardMode ? (
            <div>
              <Body fontweight="500">
                {currentWizardInvoice?.invoiceNumber}
              </Body>
              <HorizontalSpacer spacing={0.5} />
              <T800Result
                rules={assessmentResultInvoice?.latestAssessment?.rules ?? []}
                hideTitle
                clientIdentifier={clientIdentifier}
                recipientIdentifier={
                  selectedInvoiceData?.invoiceRecipient?.client?.id ??
                  selectedInvoiceData?.invoiceRecipient?.clientIdentifier ??
                  undefined
                }
                recipientName={selectedInvoiceData?.invoiceRecipient?.name}
              />
            </div>
          ) : reassessedInvoiceIds.length > 1 ? (
            <Row justify="center">
              <CheckedStatusCircle />
            </Row>
          ) : (
            <>
              {newInvoiceAssessmentApproved && (
                <Row justify="center">
                  <CheckedStatusCircle />
                </Row>
              )}
              <HorizontalSpacer spacing={1} />
              {newInvoiceAssessmentApproved ? (
                <Body>{t("invoiceAssessment.t800ApprovedDescription")}</Body>
              ) : (
                <T800Result
                  rules={assessmentResultInvoice?.latestAssessment?.rules ?? []}
                  hideTitle
                  clientIdentifier={clientIdentifier}
                  recipientIdentifier={
                    selectedInvoiceData?.invoiceRecipient?.client?.id ??
                    selectedInvoiceData?.invoiceRecipient?.clientIdentifier ??
                    undefined
                  }
                  recipientName={selectedInvoiceData?.invoiceRecipient?.name}
                />
              )}
            </>
          )}
          {(isWizardMode || !newInvoiceAssessmentApproved) &&
            reassessFailedRules.length > 0 && (
              <>
                <HorizontalSpacer spacing={1} />
                <RuleOverrideReasonFields
                  rules={reassessFailedRules}
                  reasons={ruleReasons}
                  onChange={onRuleReasonChange}
                  columns={1}
                />
              </>
            )}
          {newAssessmentError && (
            <Alert
              type="error"
              title={t("errors.somethingWentWrong")}
              message={
                <div>
                  {newAssessmentError.map((msg, i) => (
                    <div key={i}>{msg}</div>
                  ))}
                </div>
              }
            />
          )}
          <HorizontalSpacer spacing={1} />
        </Modal>
      )}
    </div>
  );
};

export default InvoiceAssessmentDetailedPage;
